import { FoodsIcon, Typography } from "@foodpilot/foods";
import { Box, IconButton, Stack, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { ReactNode } from "react";
import { CsrIndicator } from "src/api/client-api/csrStrategy/useGetAllStrategy";
import { getLeverValueColor } from "src/utils/csrStrategyValueColor";

type IndicatorRowProps = {
    indicator: CsrIndicator;
    showDivider: boolean;
    dragHandle?: ReactNode;
    onEdit?: () => void;
    onDelete?: () => void;
};

export const IndicatorRow = (props: IndicatorRowProps) => {
    const { indicator, showDivider, dragHandle, onEdit, onDelete } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const valueColor = getLeverValueColor(theme, indicator.valueColor) ?? theme.custom.grey[2800];
    const unit = indicator.unitCode.trim();
    const withUnit = (value: string) => (value ? `${value}${unit ? ` ${unit}` : ""}` : undefined);
    const notDefined = t("csr_strategy.page.goal.not_defined", "Not defined");

    return (
        <Stack
            direction="row"
            alignItems="center"
            gap={2}
            sx={{ py: 2.5, borderBottom: showDivider ? `1px solid ${theme.custom.grey[500]}` : "none" }}
        >
            {dragHandle ?? <FoodsIcon icon="drag" size={2} />}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="big-medium" component="div" sx={{ overflowWrap: "anywhere" }}>
                    {indicator.leverShortName || indicator.name}
                </Typography>
                <Typography variant="body" component="div" color={theme.custom.grey[2100]}>
                    {indicator.indicatorLabel || indicator.name}
                </Typography>
                <Stack direction="row" flexWrap="wrap" columnGap={1} color={theme.custom.grey[2100]}>
                    <Typography variant="body">
                        {t("settings.csr.overview.current_value", "Current value")}:{" "}
                        <Box component="span" sx={{ color: valueColor, fontWeight: 500 }}>
                            {withUnit(indicator.currentValue) ?? notDefined}
                        </Box>
                    </Typography>
                    <Typography aria-hidden>•</Typography>
                    <Typography variant="body">
                        {t("settings.csr.form.indicator.fields.current_campaign_goal", "Current campaign Goal")}:{" "}
                        <Box component="span" sx={{ color: theme.custom.grey[2800], fontWeight: 500 }}>
                            {withUnit(indicator.shortTermTarget) ?? notDefined}
                        </Box>
                    </Typography>
                    <Typography aria-hidden>•</Typography>
                    <Typography variant="body">
                        {t("settings.csr.form.indicator.fields.target_campaign_goal", "Target campaign Goal")}:{" "}
                        <Box component="span" sx={{ color: theme.custom.grey[2800], fontWeight: 500 }}>
                            {withUnit(indicator.longTermTarget) ?? notDefined}
                        </Box>
                    </Typography>
                </Stack>
            </Box>
            {(onEdit || onDelete) && (
                <Stack direction="row" alignItems="center" gap={0.5}>
                    {onEdit && (
                        <IconButton
                            aria-label={t("settings.csr.form.indicator.list.edit", "Edit indicator")}
                            onClick={onEdit}
                        >
                            <FoodsIcon icon="edit" size={2} />
                        </IconButton>
                    )}
                    {onDelete && (
                        <IconButton
                            aria-label={t("settings.csr.form.indicator.list.delete", "Delete indicator")}
                            onClick={onDelete}
                        >
                            <FoodsIcon icon="delete" size={2} />
                        </IconButton>
                    )}
                </Stack>
            )}
        </Stack>
    );
};
